// 左侧导航。顺序与 webui/DESIGN.md 的当前信息架构对齐。
// Logout 必须走原生 form submit（不能 fetch），daemon 会 302 redirect 回 /login。

import { Link, useRouterState } from "@tanstack/react-router";
import { useSession } from "@/hooks/useSession";
import { ConnectionBadge } from "./ConnectionBadge";
import { cn } from "@/lib/cn";

// 所有主导航项都由 TanStack Router 客户端导航；LegacyRoute 保持 never，
// 防止后续误把已下线的 daemon SSR 路由放回导航。
type MigratedRoute =
  | "/"
  | "/audit"
  | "/timeline"
  | "/accounts"
  | "/machines"
  | "/modules"
  | "/market-analysis"
  | "/vip-rank-audit"
  | "/instances"
  | "/settings"
  | "/games"
  | "/proxy-profiles";
type LegacyRoute = never;
type NavItem =
  | { migrated: true; to: MigratedRoute; label: string }
  | { migrated: false; to: LegacyRoute; label: string };

export const NAV: ReadonlyArray<NavItem> = [
  { migrated: true, to: "/", label: "仪表盘" },
  { migrated: true, to: "/instances", label: "角色" },
  { migrated: true, to: "/games", label: "游戏" },
  { migrated: true, to: "/machines", label: "机器" },
  { migrated: true, to: "/proxy-profiles", label: "代理池" },
  { migrated: true, to: "/accounts", label: "账号" },
  { migrated: true, to: "/modules", label: "模块" },
  { migrated: true, to: "/vip-rank-audit", label: "VIP审计" },
  { migrated: true, to: "/market-analysis", label: "市场分析" },
  { migrated: true, to: "/settings", label: "设置" },
  { migrated: true, to: "/timeline", label: "时间线" },
  { migrated: true, to: "/audit", label: "审计" },
];

export function Sidebar() {
  const { location } = useRouterState();
  const session = useSession();
  const actor = session.data?.actor ?? "anonymous";
  const version = session.data?.version ?? "";

  return (
    <aside className="flex h-screen w-60 flex-col overflow-hidden border-r border-[hsl(var(--border))] bg-[hsl(var(--card))]">
      <div className="shrink-0 px-4 py-4 text-sm font-semibold tracking-wide">HBH-996 操作台</div>
      <nav className="flex-1 overflow-y-auto px-2 py-2">
        <ul className="space-y-0.5">
          {NAV.map((item) => {
            const active =
              item.to === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.to);
            const className = cn(
              "block rounded px-3 py-1.5 text-sm",
              active
                ? "bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] font-medium"
                : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]",
            );
            return (
              <li key={item.to}>
                {item.migrated ? (
                  // 已迁移：客户端导航；search={{}} 让 validateSearch 用默认值补齐
                  // （audit/timeline 的 limit 都是可选 + 视图层兜底）。
                  // search as never：跨路由 union 时 TanStack 推导出 never 才合法，
                  // 给空对象 + 类型断言即可。
                  <Link
                    to={item.to}
                    search={{} as never}
                    className={className}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a href={item.to} className={className}>
                    {item.label}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
      <div className="shrink-0 border-t border-[hsl(var(--border))] px-3 py-3 text-xs text-[hsl(var(--muted-foreground))]">
        <div className="flex items-center justify-between">
          <span className="truncate" title={actor}>
            {actor}
          </span>
          <span className="font-mono">{version}</span>
        </div>
        <div className="mt-2">
          <ConnectionBadge />
        </div>
        <form method="POST" action="/logout" className="mt-2">
          <button
            type="submit"
            className="w-full rounded border border-[hsl(var(--border))] px-2 py-1 text-left hover:bg-[hsl(var(--muted))]"
          >
            退出登录
          </button>
        </form>
      </div>
    </aside>
  );
}
